// AVRASTRO AI Content Studio — reels from the advisor's own clips (requirement 07; FEASIBILITY.md §1, S2).
// Pure planning, shared by the app and the render worker:
//   - highlight selection is HEURISTIC and says so: from the transcription (ElevenLabs Scribe) it keeps whole spoken
//     sentences, preferring clear, dense speech and the opening of each clip, in the order they were filmed, until the
//     target length; clips without speech contribute an even share of their picture;
//   - captions: sentence by sentence, long sentences cut into balanced pieces that never end on an article or preposition
//     (the rule of the owner's films, tools/video/render.mjs cuesFor/GLUE), timed from the word timings;
//   - the timeline (which part of which clip, where it starts in the reel) feeds the ffmpeg render worker.
import { GLUE } from './studio-layout.ts';

export interface Word { w: string; s: number; e: number }
export interface ReelClip { id: string; duration: number; words?: Word[] | null }
export interface ReelPart { clipId: string; in: number; out: number; start: number }
export interface Cue { start: number; end: number; text: string }
export interface ReelPlan { parts: ReelPart[]; captions: Cue[]; total: number; heuristic: true }

const FILLER = /^(εε+|ε+μ+|χμ+|um+|uh+|erm+|hmm+|э+|ну|эм+)$/i;
/** the sentences of a word list (times in seconds) */
export function sentencesFromWords(words: readonly Word[]): Word[][] {
  const out: Word[][] = []; let cur: Word[] = [];
  for (const w of words) {
    cur.push(w);
    if (/[.;!?·;:»"”]$/.test(w.w) || (cur.length >= 2 && w.s - cur[cur.length - 2]!.e > 0.9)) { out.push(cur); cur = []; }
  }
  if (cur.length) out.push(cur);
  return out;
}

/** caption cues of a sentence: pieces of at most maxChars, cut near equal lengths, preferring commas, never after glue words */
export function cuesForWords(words: readonly Word[], maxChars: number, offset = 0): Cue[] {
  const out: Cue[] = [];
  for (const sw of sentencesFromWords(words)) {
    const cum: number[] = []; let c = 0; for (const w of sw) { c += w.w.length + 1; cum.push(c); }
    const k = Math.max(1, Math.ceil(c / maxChars)), target = c / k;
    const cuts: number[] = []; let from = 0;
    for (let j = 1; j < k; j++) {
      let best = -1, score = Infinity;
      for (let i = from; i < sw.length - 1; i++) {
        const d = Math.abs(cum[i]! - target * j) - (/[,·]$/.test(sw[i]!.w) ? target * 0.3 : 0) + (GLUE.test(sw[i]!.w.replace(/[«»"“”,.:;!?]/g, '')) ? target * 0.5 : 0);
        if (d < score && cum[i]! - (from ? cum[from - 1]! : 0) >= 8 && c - cum[i]! >= 10) { score = d; best = i; }
      }
      if (best < 0) break;
      cuts.push(best); from = best + 1;
    }
    let start = 0;
    for (const cut of [...cuts, sw.length - 1]) {
      const part = sw.slice(start, cut + 1);
      if (part.length) out.push({ start: round(offset + part[0]!.s), end: round(offset + part[part.length - 1]!.e + 0.15), text: part.map((x) => x.w).join(' ') });
      start = cut + 1;
    }
  }
  for (let i = 0; i < out.length - 1; i++) out[i]!.end = Math.min(out[i]!.end, out[i + 1]!.start);
  return out;
}
const round = (x: number) => Math.round(x * 1000) / 1000;

/** pick the highlights of the clips for a reel of about `target` seconds (at most 60 s) */
export function planReel(clips: readonly ReelClip[], target = 30, o: { maxChars?: number } = {}): ReelPlan {
  const goal = Math.min(60, Math.max(5, target));
  type Cand = { clipId: string; clipIndex: number; in: number; out: number; words: Word[]; score: number };
  const cands: Cand[] = [];
  clips.forEach((cl, ci) => {
    const words = (cl.words ?? []).filter((w) => w.e > w.s && w.s >= 0 && w.e <= cl.duration + 0.5);
    if (!words.length) return;
    sentencesFromWords(words).forEach((s, si) => {
      const inT = Math.max(0, s[0]!.s - 0.25), outT = Math.min(cl.duration, s[s.length - 1]!.e + 0.35);
      const dur = outT - inT;
      if (dur < 0.8) return;
      const real = s.filter((w) => !FILLER.test(w.w.replace(/[.,!?;:]/g, '')));
      if (real.length < 2) return; // hesitations and single words are never a highlight
      const density = real.length / dur; // clear speech: words per second without fillers
      const score = Math.min(density, 3.2) + (si === 0 ? 0.6 : 0) - (s.length - real.length) * 0.4 - (dur > 12 ? 0.8 : 0);
      cands.push({ clipId: cl.id, clipIndex: ci, in: inT, out: outT, words: s, score });
    });
  });
  const chosen: Cand[] = [];
  let len = 0;
  for (const cd of [...cands].sort((a, b) => b.score - a.score)) {
    const d = cd.out - cd.in;
    if (len + d > goal + 2) continue;
    chosen.push(cd); len += d;
    if (len >= goal - 1) break;
  }
  // clips without speech: an even share of their picture fills what is left (b-roll)
  const silent = clips.map((c, i) => ({ c, i })).filter(({ c }) => !(c.words ?? []).length && c.duration > 0.5);
  if (silent.length && len < goal) {
    const each = Math.max(1.5, (goal - len) / silent.length);
    for (const { c, i } of silent) { const d = Math.min(c.duration, each); if (len + d > goal + 2) break; chosen.push({ clipId: c.id, clipIndex: i, in: Math.max(0, (c.duration - d) / 2), out: Math.max(0, (c.duration - d) / 2) + d, words: [], score: 0 }); len += d; }
  }
  // in filmed order
  chosen.sort((a, b) => a.clipIndex - b.clipIndex || a.in - b.in);
  const parts: ReelPart[] = []; const captions: Cue[] = [];
  let t = 0;
  for (const cd of chosen) {
    parts.push({ clipId: cd.clipId, in: round(cd.in), out: round(cd.out), start: round(t) });
    const shifted = cd.words.map((w) => ({ w: w.w, s: w.s - cd.in, e: w.e - cd.in }));
    captions.push(...cuesForWords(shifted, o.maxChars ?? 34, t));
    t += cd.out - cd.in;
  }
  return { parts, captions, total: round(t), heuristic: true };
}

/** captions of a voice-over (ElevenLabs word timings), placed from `offset` seconds */
export const voiceCaptions = (words: readonly Word[], offset = 0, maxChars = 34) => cuesForWords(words, maxChars, offset);

/** WebVTT of cues (for the horizontal version, as the owner's films do) */
export function toWebVtt(cues: readonly Cue[]): string {
  const ts = (x: number) => { const h = Math.floor(x / 3600), m = Math.floor((x % 3600) / 60), r = (x % 60).toFixed(3).padStart(6, '0'); return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${r}`; };
  return 'WEBVTT\n\n' + cues.map((c, k) => `${k + 1}\n${ts(c.start)} --> ${ts(c.end)}\n${c.text}\n`).join('\n');
}
